import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { companiesApi } from '@/lib/api/endpoints';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '@/stores/auth-store';
import { useCompanyScopeStore } from '@/stores/company-scope-store';

/**
 * Lets the platform owner work inside any tenant. Renders for nobody else: the
 * backend refuses X-Company-Id from a tenant user, so showing this to them
 * would only offer an action that cannot succeed.
 *
 * Switching clears the query cache outright rather than invalidating keys --
 * every cached list belongs to the company that was active when it was fetched,
 * and showing one tenant's vehicles under another's name, even for a frame, is
 * worse than a brief reload.
 */
export function CompanySwitcher() {
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const { scope, select } = useCompanyScopeStore();
  const [open, setOpen] = useState(false);

  const isPlatformAdmin = user?.role === 'PLATFORM_ADMIN';

  const { data, isLoading } = useQuery({
    queryKey: ['companies', 'switcher'],
    queryFn: () => companiesApi.list({ limit: 100 }),
    enabled: isPlatformAdmin,
    staleTime: 5 * 60_000,
  });

  if (!isPlatformAdmin) return null;

  const companies = data?.items ?? [];
  const label = scope?.name ?? 'My company';

  const choose = (next: { id: string; name: string } | null) => {
    setOpen(false);
    if ((next?.id ?? null) === (scope?.id ?? null)) return;
    select(next);
    queryClient.clear();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label="Switch company"
          className={cn('h-9 max-w-[200px] gap-2', scope && 'border-primary/40 bg-primary/5')}
        >
          <Building2 className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate text-xs">{label}</span>
          <ChevronsUpDown className="ml-auto size-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[260px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Find a company…" className="h-9" />
          <CommandList>
            <CommandEmpty>{isLoading ? 'Loading…' : 'No companies found.'}</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__own__" onSelect={() => choose(null)}>
                <Check className={cn('size-3.5', scope ? 'opacity-0' : 'opacity-100')} />
                My company
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Tenants">
              {companies.map((company) => (
                <CommandItem
                  key={company.id}
                  value={`${company.name} ${company.slug}`}
                  onSelect={() => choose({ id: company.id, name: company.name })}
                >
                  <Check
                    className={cn(
                      'size-3.5',
                      scope?.id === company.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{company.name}</span>
                  {!company.isActive && (
                    <span className="ml-auto text-[10px] text-muted-foreground">inactive</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
